/**
 * The legal form a business partner trades under, and what it decides about
 * the billing details we need from them.
 *
 * Values are colugo's `BusinessType` enum, verbatim: they are what the API
 * accepts and what the database stores.
 */

export type FieldSet = 'privatePerson' | 'partnership' | 'corporation';

/**
 * Copy in every language a Vytal app speaks. English and German are complete;
 * a language without its own text falls back to English.
 */
export type Copy = {
  readonly en: string;
  readonly de: string;
  readonly fr?: string;
  readonly es?: string;
  readonly nl?: string;
};

/** Takes any language tag (`de`, `de-AT`, `DE`); unknown languages read English. */
function textIn(copy: Copy, language: string): string {
  const code = language.toLowerCase().slice(0, 2) as keyof Copy;
  return copy[code] ?? copy.en;
}

const DEFINITIONS = {
  sole_proprietorship: {
    fieldSet: 'privatePerson',
    label: { en: 'Sole Proprietorship', de: 'Einzelunternehmen' },
  },
  partnership: {
    fieldSet: 'partnership',
    label: {
      en: 'Partnership (GbR, SNC)',
      de: 'Personengesellschaft (GbR, SNC)',
    },
  },
  limited_partnership: {
    fieldSet: 'partnership',
    label: {
      en: 'Limited Partnership (KG, SCS)',
      de: 'Kommanditgesellschaft (KG, SCS)',
    },
  },
  private_limited_company: {
    fieldSet: 'corporation',
    label: {
      en: 'Private Limited Company (GmbH, SARL, Ltd)',
      de: 'Gesellschaft mit beschränkter Haftung (GmbH, SARL, Ltd)',
    },
  },
  public_limited_company: {
    fieldSet: 'corporation',
    label: {
      en: 'Public Limited Company (AG, SA, PLC)',
      de: 'Aktiengesellschaft (AG, SA, PLC)',
    },
  },
  non_profit_organization: {
    fieldSet: 'corporation',
    label: {
      en: 'Non-Profit Organization (NGOs, e.V., Associations)',
      de: 'Gemeinnützige Organisation (NGO, e.V., Verband)',
    },
  },
  government_entity_public_institution: {
    fieldSet: 'corporation',
    label: {
      en: 'Government Entity / Public Institution',
      de: 'Behörde / Öffentliche Institution',
    },
  },
  cooperative: {
    fieldSet: 'corporation',
    label: { en: 'Cooperative (eG, SCOP)', de: 'Genossenschaft (eG, SCOP)' },
  },
  trust_foundation: {
    fieldSet: 'corporation',
    label: { en: 'Trust / Foundation', de: 'Stiftung / Trust' },
  },
} as const satisfies Record<string, { fieldSet: FieldSet; label: Copy }>;

export type LegalForm = keyof typeof DEFINITIONS;

/** The legal forms whose field set is `S`. */
export type LegalFormIn<S extends FieldSet> = {
  [K in LegalForm]: (typeof DEFINITIONS)[K]['fieldSet'] extends S ? K : never;
}[LegalForm];

/** In the order a picker lists them. */
export const LEGAL_FORMS: readonly LegalForm[] = [
  'sole_proprietorship',
  'partnership',
  'limited_partnership',
  'private_limited_company',
  'public_limited_company',
  'non_profit_organization',
  'government_entity_public_institution',
  'cooperative',
  'trust_foundation',
];

export function isLegalForm(value: unknown): value is LegalForm {
  return typeof value === 'string' && Object.hasOwn(DEFINITIONS, value);
}

export function legalFormLabel(form: LegalForm, language: string): string {
  return textIn(DEFINITIONS[form].label, language);
}

export function fieldSetOf(form: LegalForm): FieldSet {
  return DEFINITIONS[form].fieldSet;
}

export function legalFormsIn<S extends FieldSet>(
  fieldSet: S,
): [LegalFormIn<S>, ...LegalFormIn<S>[]] {
  return LEGAL_FORMS.filter((form) => fieldSetOf(form) === fieldSet) as [
    LegalFormIn<S>,
    ...LegalFormIn<S>[],
  ];
}

/**
 * The billing-details vocabulary. Each consumer maps these onto its own field
 * names (the admin form's camelCase, colugo's snake_case input).
 */
export type BillingField =
  | 'legalForm'
  | 'firstName'
  | 'lastName'
  | 'companyName'
  | 'email'
  | 'phoneNumber'
  | 'street'
  | 'addressExtra'
  | 'zip'
  | 'city'
  | 'countryCode'
  | 'state'
  | 'dateOfBirth'
  | 'vatId'
  | 'corporateStructureInfo';

/** Asked of every legal form, in form order. */
export const COMMON_REQUIRED_FIELDS = [
  'firstName',
  'lastName',
  'companyName',
  'email',
  'phoneNumber',
  'street',
  'zip',
  'city',
  'countryCode',
] as const satisfies readonly BillingField[];

export const COMMON_OPTIONAL_FIELDS = [
  'addressExtra',
  'state',
] as const satisfies readonly BillingField[];

/**
 * A field set relabels the contact fields because for a company they describe
 * its billing contact rather than the owner. `billingCopy` gives the text.
 */
export type FieldLabelKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phoneNumber'
  | 'billingName'
  | 'billingSurname'
  | 'billingEmail'
  | 'companyEmail'
  | 'companyPhoneNumber';

export type AddressHeadingKey = 'billingAddress' | 'companyAddress';

/** Who may act for the business, asked as free text in `corporateStructureInfo`. */
export type RepresentativesKey = 'liablePartners' | 'authorizedSignatories';

export const FIELD_SETS = {
  privatePerson: {
    requiredFields: ['dateOfBirth'],
    optionalFields: [],
    labels: {
      firstName: 'firstName',
      lastName: 'lastName',
      email: 'email',
      phoneNumber: 'phoneNumber',
    },
    addressHeading: 'billingAddress',
    representatives: null,
  },
  partnership: {
    requiredFields: ['vatId'],
    optionalFields: ['corporateStructureInfo'],
    labels: {
      firstName: 'billingName',
      lastName: 'billingSurname',
      email: 'billingEmail',
      phoneNumber: 'companyPhoneNumber',
    },
    addressHeading: 'billingAddress',
    representatives: 'liablePartners',
  },
  corporation: {
    requiredFields: ['vatId'],
    optionalFields: ['corporateStructureInfo'],
    labels: {
      firstName: 'billingName',
      lastName: 'billingSurname',
      email: 'companyEmail',
      phoneNumber: 'companyPhoneNumber',
    },
    addressHeading: 'companyAddress',
    representatives: 'authorizedSignatories',
  },
} as const satisfies Record<
  FieldSet,
  {
    requiredFields: readonly BillingField[];
    optionalFields: readonly BillingField[];
    labels: Record<
      'firstName' | 'lastName' | 'email' | 'phoneNumber',
      FieldLabelKey
    >;
    addressHeading: AddressHeadingKey;
    representatives: RepresentativesKey | null;
  }
>;

/** The fields a field set adds to the common ones, required or not. */
export type ExtraFieldOf<S extends FieldSet> =
  | (typeof FIELD_SETS)[S]['requiredFields'][number]
  | (typeof FIELD_SETS)[S]['optionalFields'][number];

export function requiredFields(form: LegalForm): BillingField[] {
  return [
    'legalForm',
    ...COMMON_REQUIRED_FIELDS,
    ...FIELD_SETS[fieldSetOf(form)].requiredFields,
  ];
}

const isBlank = (value: unknown) =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '');

/**
 * The required fields `values` leaves empty, in form order. Without a valid
 * legal form only `legalForm` is reported, since it decides the rest.
 */
export function missingFields(
  form: string | null | undefined,
  values: Partial<Record<BillingField, unknown>>,
): BillingField[] {
  if (!isLegalForm(form)) return ['legalForm'];
  return requiredFields(form).filter(
    (field) => field !== 'legalForm' && isBlank(values[field]),
  );
}

/** Everything a field set words differently, as `billingCopy` keys. */
export type BillingCopyKey =
  | FieldLabelKey
  | AddressHeadingKey
  | RepresentativesKey
  | `${RepresentativesKey}${'Instructions' | 'Example' | 'Placeholder'}`;

const BILLING_COPY: Record<BillingCopyKey, Copy> = {
  firstName: { en: 'Name', de: 'Vorname' },
  lastName: { en: 'Surname', de: 'Nachname' },
  email: { en: 'Email', de: 'Email' },
  phoneNumber: { en: 'Phone Number', de: 'Telefonnummer' },
  billingName: { en: 'Billing name', de: 'Rechnungsname' },
  billingSurname: { en: 'Billing surname', de: 'Rechnungsnachname' },
  billingEmail: { en: 'Billing email', de: 'Rechnungs-E-Mail' },
  companyEmail: { en: 'Company email', de: 'Firmen-E-Mail' },
  companyPhoneNumber: {
    en: 'Company phone number',
    de: 'Firmen-Telefonnummer',
  },
  billingAddress: { en: 'Billing Address', de: 'Rechnungsadresse' },
  companyAddress: { en: 'Company address', de: 'Firmenadresse' },
  liablePartners: { en: 'Liable partners', de: 'Haftende Gesellschafter' },
  liablePartnersInstructions: {
    en: 'Please enter the name and address of each liable partner in the following format:',
    de: 'Bitte geben Sie Name und Adresse jedes haftenden Gesellschafters im folgenden Format ein:',
  },
  liablePartnersExample: {
    en: 'Example: Jane Mann (21.11.1991), Examplestreet 3, 1111 City, Region, Country',
    de: 'Beispiel: Jane Mann (21.11.1991), Beispielstraße 3, 1111 Stadt, Region, Land',
  },
  liablePartnersPlaceholder: {
    en: 'Jane Mann (21.11.1991), Examplestreet 3, 1111 City, Region, Country',
    de: 'Jane Mann (21.11.1991), Beispielstraße 3, 1111 Stadt, Region, Land',
  },
  authorizedSignatories: {
    en: 'Authorized signatories',
    de: 'Vertretungsberechtigte',
  },
  authorizedSignatoriesInstructions: {
    en: 'Please enter the name and date of birth of each authorized signatory in the following format:',
    de: 'Bitte geben Sie Name und Geburtsdatum jeder vertretungsberechtigten Person im folgenden Format ein:',
  },
  authorizedSignatoriesExample: {
    en: 'Example: Jane Mann (21.11.1991), John Woman (01.01.2002)',
    de: 'Beispiel: Jane Mann (21.11.1991), John Woman (01.01.2002)',
  },
  authorizedSignatoriesPlaceholder: {
    en: 'Jane Mann (21.11.1991), John Woman (01.01.2002)',
    de: 'Jane Mann (21.11.1991), John Woman (01.01.2002)',
  },
};

export function billingCopy(key: BillingCopyKey, language: string): string {
  return textIn(BILLING_COPY[key], language);
}
